// Simplified data store functions for frontend2
// Based on frontend/src/stores/data.store.tsx

import ApiService from './api.service';

export type ChatThread = {
  thread_id: string;
  name: string;
  agent_type: string;
  created_by: string | null;  // null for agent-initiated conversations
  created_at: string;
  updated_at: string;
  shared_with: string[];
  is_owner: boolean;
};

export type ChatThreadMessage = {
  id: string;
  role: 'user' | 'assistant' | 'tool';
  content: string;
  tool_call_id?: string;
  name?: string;
  tool_calls?: Array<{
    id: string;
    name: string;
    args: any;
  }>;
}

// Mirrors the backend Verbosity StrEnum (src/models/ai_session.py).
// high = full narration, all tool calls render; medium = brief narration;
// low = terse responses + background tool calls hidden.
export type Verbosity = 'high' | 'medium' | 'low';

// Mirrors the backend ReasoningEffort StrEnum (src/models/ai_session.py) —
// adaptive thinking effort. Only has an effect on models that support it;
// the backend silently ignores it for the rest.
export type ReasoningEffort = 'max' | 'high' | 'medium' | 'low';

// Per-thread settings persisted on the backend AiThread doc.
// Fields are optional: null/undefined means "not set" and the frontend
// falls back to the agent's default.
export type ChatThreadSettings = {
  verbosity?: Verbosity | null;
  model?: string | null;
  reasoning?: ReasoningEffort | null;
  // Admin/debug: store a summary of the model's reasoning on each LLM usage
  // row (backend AiThreadSettings.capture_thinking). Snake_case: sent verbatim.
  capture_thinking?: boolean | null;
};

export type ChatThreadMessagesResult = {
  messages: ChatThreadMessage[];
  settings?: ChatThreadSettings | null;
};

export const getChatThreadMessages = async (threadId: string): Promise<ChatThreadMessagesResult> => {
  try {
    const result = (await ApiService.getInstance().request('GET', `/chat/threads/${threadId}/messages`)) as {
      thread_id: string;
      messages: ChatThreadMessage[];
      settings?: ChatThreadSettings | null;
    };
    return {
      messages: result.messages,
      settings: result.settings,
    };
  } catch (error: any) {
    // 404 is expected for new threads that don't exist yet
    // The thread will be created automatically when the first message is sent
    if (error?.code === 404) {
      return {
        messages: [],
      };
    }
    // Re-throw other errors
    throw error;
  }
};

export const updateChatThreadSettings = async (threadId: string, settings: ChatThreadSettings): Promise<void> => {
  await ApiService.getInstance().request('PATCH', `/chat/threads/${threadId}`, { settings });
};

export type ChatModel = {
  id: string;
  name: string;
  supportsReasoning: boolean;
  // Largest output budget selectable for this model, and what the platform
  // default resolves to for it.
  maxOutputTokens: number;
  defaultOutputTokens: number;
};

export const getAvailableModels = async (): Promise<ChatModel[]> => {
  const result = (await ApiService.getInstance().request('GET', '/models')) as {
    data: Array<{
      id: string;
      name?: string;
      supports_reasoning?: boolean;
      max_output_tokens?: number;
      default_output_tokens?: number;
    }>;
  };
  return result.data.map((m) => ({
    id: m.id,
    name: m.name || m.id,
    supportsReasoning: m.supports_reasoning ?? false,
    maxOutputTokens: m.max_output_tokens ?? 16384,
    defaultOutputTokens: m.default_output_tokens ?? 16384,
  }));
};

// The model new chats start on when the tenant hasn't picked one in
// Settings → Ellie. Resolved per tenant server-side: Sonnet 5.5 when the
// tenant's Snowflake account serves it, otherwise Sonnet 5.
export const getPlatformDefaultModel = async (): Promise<{ id: string; name: string }> => {
  return (await ApiService.getInstance().request('GET', '/models/default')) as { id: string; name: string };
};

export type ChatThreadsPage = {
  threads: ChatThread[];
  hasMore: boolean;
};

/**
 * Whose conversations to list. 'mine' is the caller's own threads plus anything
 * shared with them; 'all' is every thread in the tenant and is Admin-only —
 * the backend narrows it to 'mine' for everyone else.
 */
export type ChatThreadScope = 'mine' | 'all';

export const listChatThreads = async (
  agentType?: string | string[],
  limit: number = 50,
  scope: ChatThreadScope = 'mine',
  skip: number = 0,
): Promise<ChatThreadsPage> => {
  const params: Record<string, any> = { limit, skip, scope };
  // One paginated query can span multiple agent types (comma-separated), so
  // a merged history (e.g. ellie + analyst) pages correctly as one list.
  const types = Array.isArray(agentType) ? agentType : agentType ? [agentType] : [];
  if (types.length) {
    params.agent_type = types.join(',');
  }
  const result = (await ApiService.getInstance().request('GET', '/chat/threads', params)) as {
    threads: ChatThread[];
    has_more?: boolean;
  };
  return { threads: result.threads, hasMore: result.has_more ?? false };
};

// Simple cache invalidation - in a real app you'd use React Query
export const invalidateChatThreads = () => {
  // This is a no-op in the simplified version
  // In the full version, this would invalidate React Query cache
};

export type CompactThreadResult = {
  thread_id: string;
  messages: ChatThreadMessage[];
  compacted: boolean;
  original_message_count: number;
  new_message_count: number;
};

export const compactThread = async (threadId: string): Promise<CompactThreadResult> => {
  const result = await ApiService.getInstance().request('POST', `/chat/threads/${threadId}/compact`);
  return result as CompactThreadResult;
};

